import { SensorDataRestoringRequest } from "@shared/models";
import { call, put, takeLatest } from "redux-saga/effects";
import {
  PaginatedResponse,
  ActionWithPayload,
  WithCallback,
  SensorDataRestoringRequestFilter,
} from "@shared/interfaces";
import { startLoading, stopLoading } from "@shared/store/actions";
import { downLoadFileFromUrl } from "@shared/utils";

import api from "../api";
import {
  createSensorDataRestoringRequest,
  deleteSensorDataRestoringRequest,
  downloadSensorDataRestoringRequest,
  getSensorDataRestoringRequest,
  getSensorDataRestoringRequests,
  restoreSensorDataRestoringRequest,
} from "./actions";
import {
  CreateSensorDataRestoringRequestRequest,
  CreateSensorDataRestoringRequestResponse,
  DeleteSensorDataRestoringRequestRequest,
  DeleteSensorDataRestoringRequestResponse,
  DownloadSensorDataRestoringRequestRequest,
  DownloadSensorDataRestoringRequestResponse,
  GetSensorDataRestoringRequestRequest,
  RestoreSensorDataRestoringRequestRequest,
  RestoreSensorDataRestoringRequestResponse,
} from "../interfaces";

function* getSensorDataRestoringRequestsSaga({ payload }: ActionWithPayload<SensorDataRestoringRequestFilter>) {
  try {
    yield put(startLoading());
    const response: PaginatedResponse<SensorDataRestoringRequest> = yield call(
      api.getSensorDataRestoringRequests,
      payload,
    );
    yield put(stopLoading());
    yield put(
      getSensorDataRestoringRequests.success({
        ...response,
        is_clear: !payload.page,
      }),
    );
  } catch (error) {
    yield put(getSensorDataRestoringRequests.failure(error as Error));
    yield put(stopLoading());
  }
}

function* getSensorDataRestoringRequestSaga({ payload }: ActionWithPayload<GetSensorDataRestoringRequestRequest>) {
  try {
    yield put(startLoading());
    const response: SensorDataRestoringRequest = yield call(api.getSensorDataRestoringRequest, payload.id);
    yield put(stopLoading());
    yield put(getSensorDataRestoringRequest.success(response));
  } catch (error) {
    yield put(getSensorDataRestoringRequest.failure(error as Error));
    yield put(stopLoading());
  }
}

function* downloadRensorDataRestoringRequestSaga({
  payload,
}: ActionWithPayload<DownloadSensorDataRestoringRequestRequest>) {
  try {
    yield put(startLoading());
    const response: DownloadSensorDataRestoringRequestResponse = yield call(
      api.downloadSensorDataRestoringRequest,
      payload.id,
    );

    downLoadFileFromUrl(response.url);

    yield put(stopLoading());
    yield put(downloadSensorDataRestoringRequest.success(response));
  } catch (error) {
    yield put(downloadSensorDataRestoringRequest.failure(error as Error));
    yield put(stopLoading());
  }
}

function* restoreSensorDataRestoringRequestSaga({
  payload,
}: ActionWithPayload<WithCallback<RestoreSensorDataRestoringRequestRequest>>) {
  try {
    yield put(startLoading());
    const response: RestoreSensorDataRestoringRequestResponse = yield call(
      api.restoreSensorDataRestoringRequest,
      payload.id,
    );

    yield put(restoreSensorDataRestoringRequest.success(response));
  } catch (error) {
    yield put(restoreSensorDataRestoringRequest.failure(error as Error));
  } finally {
    yield put(stopLoading());
    payload.cb?.();
  }
}

function* deleteSensorDataRestoringRequestSaga({
  payload,
}: ActionWithPayload<DeleteSensorDataRestoringRequestRequest>) {
  try {
    yield put(startLoading());
    const response: DeleteSensorDataRestoringRequestResponse = yield call(
      api.deleteSensorDataRestoringRequest,
      payload.id,
    );
    yield put(stopLoading());
    yield put(deleteSensorDataRestoringRequest.success(response));
  } catch (error) {
    yield put(deleteSensorDataRestoringRequest.failure(error as Error));
    yield put(stopLoading());
  }
}

function* createSensorDataRestoringRequestSaga({
  payload,
}: ActionWithPayload<WithCallback<CreateSensorDataRestoringRequestRequest>>) {
  try {
    const { cb, ...rest } = payload;
    yield put(startLoading());
    const response: CreateSensorDataRestoringRequestResponse = yield call(api.createSensorDataRestoringRequest, rest);

    yield put(stopLoading());
    yield put(createSensorDataRestoringRequest.success(response));
    cb && cb();
  } catch (error) {
    yield put(createSensorDataRestoringRequest.failure(error as Error));
    yield put(stopLoading());
  }
}

function* SensorDataRestoringRequestSaga() {
  yield takeLatest(getSensorDataRestoringRequests.request, getSensorDataRestoringRequestsSaga);
  yield takeLatest(getSensorDataRestoringRequest.request, getSensorDataRestoringRequestSaga);
  yield takeLatest(createSensorDataRestoringRequest.request, createSensorDataRestoringRequestSaga);
  yield takeLatest(deleteSensorDataRestoringRequest.request, deleteSensorDataRestoringRequestSaga);
  yield takeLatest(downloadSensorDataRestoringRequest.request, downloadRensorDataRestoringRequestSaga);
  yield takeLatest(restoreSensorDataRestoringRequest.request, restoreSensorDataRestoringRequestSaga);
}

export default SensorDataRestoringRequestSaga;
